import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import { privateProviderLabel } from "../../../../../../../../data/buildingPermit/fieldDatas";
import { fieldsToCheckPrivateProvider } from "../../../../../../../../data/permitFormsData";
import { companyPrivateProvider } from "../../../../../../../../data/privateProvider";

// Field names that mirror a provider record
const PP_FIELD_NAMES = [
  "pp_company_name",
  "pp_address",
  "pp_phone",
  "pp_email",
  "pp_name",
  "pp_license",
];

const RenderPrivateProvider = ({
  full_title,
  privateProvider,
  onChange,
  fields,
}) => {
  const hydratedIdRef = useRef(null); // tracks which provider ID has been hydrated

  const savedProjects = useSelector((state) => state.nocData?.saved_projects);
  const { createDocLoading, addingNewPrivateProvider } = useSelector(
    (state) => state.buildingState,
  );

  const [loadingData, setLoadingData] = useState(true);

  // Build provider list once per savedProjects change
  const privateProviderList = useMemo(() => {
    const saved = savedProjects?.privateProvider || [];
    return [companyPrivateProvider, ...saved];
  }, [savedProjects?.privateProvider]);

  useEffect(() => {
    setLoadingData(false);
  }, [privateProviderList]);

  // Index fields by name once per render — O(1) lookups below
  const fieldMap = useMemo(
    () => Object.fromEntries((fields || []).map((f) => [f.name, f])),
    [fields],
  );

  // Hydrate dependent fields when the list loads and a value is already selected.
  // Skipped if we already hydrated this provider ID (avoids overwriting user edits).
  useEffect(() => {
    if (!privateProviderList.length || !privateProvider) {
      if (!privateProvider) hydratedIdRef.current = null;
      return;
    }
    if (hydratedIdRef.current === privateProvider?.value) return; // already hydrated

    const selected = privateProviderList.find(
      (p) => p.id === privateProvider?.value,
    );

    if (!selected) {
      // Provider no longer exists — clear everything
      onChange("privateProvider", "");
      PP_FIELD_NAMES.forEach((name) => onChange(name, ""));
    } else {
      // Only populate fields that are empty (don't overwrite user edits)
      PP_FIELD_NAMES.forEach((name) => {
        const current = (fieldMap[name]?.value || "").trim();
        if (!current) onChange(name, selected[name] || "");
      });
    }

    hydratedIdRef.current = privateProvider?.value;
  }, [privateProviderList, privateProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;
    const selected = privateProviderList.find((p) => p.id === selectedId);
    onChange("privateProvider", {
      value: selectedId,
      selected,
      label: selected?.["pp_company_name"],
    });
    PP_FIELD_NAMES.forEach((name) => onChange(name, selected?.[name] || ""));

    hydratedIdRef.current = selectedId; // mark as hydrated so the effect skips it
  };

  const selectedPro = useMemo(
    () => privateProviderList.find((p) => p.id === privateProvider?.value),
    [privateProviderList, privateProvider],
  );

  if (loadingData) return <Skeleton variant="rectangular" height={120} />;

  return (
    <>
      <Typography align="center" mt={2} mb={0} variant="h6">
        {full_title}
      </Typography>

      <FormControl
        fullWidth
        margin="normal"
        variant="filled"
        sx={{
          "& .MuiFilledInput-root": {
            border: privateProvider ? "2px solid #004976" : undefined,
            borderRadius: "14px",
            backgroundColor: "transparent",
          },
          "& .MuiFilledInput-underline:before": { borderBottom: "none" },
          "& .MuiFilledInput-underline:after": { borderBottom: "none" },
        }}
      >
        <InputLabel>Select Private Provider</InputLabel>
        <Select
          disabled={createDocLoading || addingNewPrivateProvider}
          name="privateProvider"
          value={privateProvider?.value || ""}
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: "1.5px",
                maxHeight: 200,
                width: "auto",
                maxWidth: "100%",
              },
            },
          }}
        >
          <MenuItem disabled value="">
            {privateProviderList.length === 0
              ? "No records found"
              : "Select One"}
          </MenuItem>
          {privateProviderList.map((option, i) => (
            <MenuItem key={option.id + i} value={option.id}>
              {option.pp_company_name || "Unnamed"}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {privateProvider && selectedPro && (
        <>
          {fieldsToCheckPrivateProvider.map((field, i) => (
            <TextField
              key={field + i}
              fullWidth
              margin="normal"
              variant="filled"
              label={privateProviderLabel[field]}
              name={field}
              value={
                field === "pp_company_name"
                  ? privateProvider?.label
                  : fieldMap[field]?.value || ""
              }
              disabled={!addingNewPrivateProvider}
            />
          ))}
        </>
      )}
    </>
  );
};

export default RenderPrivateProvider;
